import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getTestId } from '../hooks/useTestId';
import { AccordionItem } from '../components/ui/Accordion';
import UWEFormContext from '../context/UWEFormContext';
import { useDarkMode } from '../context/DarkModeContext';
import Icon from 'src/components/Icon';
import { useConflictResolver } from './useConflictResolver';

const StyledFieldItem = styled.div`
  padding: 0.5rem 0.625rem;
  fontsize: 0.82rem;

  &:nth-of-type(odd) {
    background: ${props => (props.darkMode ? '#32363d' : props.theme.gray100)};
  }
`;

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(Infinity);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// `indeterminate` (the "some selected" state) has no HTML attribute, so it is set on the element.
function SelectCheckbox({ checked, indeterminate = false, onChange, label, testId = {} }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <Input type="checkbox" ref={ref} className="m-0 flex-shrink-0" checked={checked} onChange={onChange} aria-label={label} {...testId} />;
}

function fieldLabel(fieldId, field) {
  return field?.description || field?.name || fieldId;
}

/** `selection` is null when the panel is read-only; otherwise { selected: Set, toggleKey(key) }. */
function ConflictGroupContent({ items, navigateToField, selection }) {
  const [ref, width] = useContainerWidth();
  const compact = width < 500;
  const { isDarkMode } = useDarkMode();

  const rowCheckbox = (fieldId, field) =>
    selection && (
      <SelectCheckbox
        checked={selection.selected.has(fieldId)}
        onChange={() => selection.toggleKey(fieldId)}
        label={t`Select ${fieldLabel(fieldId, field)}`}
        testId={getTestId('conflicts', '.', 'select', '-', fieldId)}
      />
    );

  return (
    <div ref={ref} style={{ maxHeight: '16rem', overflowY: 'auto' }}>
      {compact ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(({ fieldId, conflict, field }) => {
            const accountNumber = field?.dataPath?.split('.').pop() ?? '';
            const label = fieldLabel(fieldId, field);
            return (
              <StyledFieldItem key={fieldId} darkMode={isDarkMode}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
                  {rowCheckbox(fieldId, field)}
                  <code>{accountNumber}</code>
                  <span style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                  <Button
                    size="sm"
                    color="link"
                    className="p-0 flex-shrink-0"
                    onClick={() => navigateToField(fieldId, 'warning')}
                    {...getTestId('conflicts', '.', 'navigate', '-', fieldId)}
                  >
                    <Icon icon="Crosshair" />
                  </Button>
                </div>
                <div style={{ fontSize: '0.78rem', color: isDarkMode ? '#878b9a' : '#6c757d' }}>
                  <Trans>Calculated</Trans>: <span className="font-bold text-success">{conflict.calculated ?? '—'}</span>
                  <span style={{ marginLeft: '1rem' }}>
                    <Trans>Reported</Trans>: <span className="font-bold text-warning">{conflict.reported ?? '—'}</span>
                  </span>
                </div>
              </StyledFieldItem>
            );
          })}
        </div>
      ) : (
        <Table borderless striped size="sm" className="mb-0" style={{ fontSize: '0.82rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: isDarkMode ? '#32363d' : 'white', zIndex: 1 }}>
            <tr>
              {selection && <th style={{ width: '1.5rem' }} />}
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Account</Trans>
              </th>
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Field</Trans>
              </th>
              <th className="text-center text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Calculated</Trans>
              </th>
              <th className="text-center text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Reported</Trans>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map(({ fieldId, conflict, field }) => {
              const accountNumber = field?.dataPath?.split('.').pop() ?? '';
              return (
                <tr key={fieldId}>
                  {selection && <td className="align-middle">{rowCheckbox(fieldId, field)}</td>}
                  <td className="whitespace-nowrap">
                    <code>{accountNumber}</code>
                  </td>
                  <td>
                    {fieldLabel(fieldId, field)}
                    {field?.subformTitle && (
                      <div className="text-muted" style={{ fontSize: '0.75em' }}>
                        {field.subformTitle}
                      </div>
                    )}
                  </td>
                  <td className="text-center text-success font-bold">{conflict.calculated ?? '—'}</td>
                  <td className="text-center text-warning font-bold">{conflict.reported ?? '—'}</td>
                  <td className="text-center" style={{ width: '2rem' }}>
                    <Button
                      size="sm"
                      color="link"
                      className="p-0"
                      onClick={() => navigateToField(fieldId, 'warning')}
                      {...getTestId('conflicts', '.', 'navigate', '-', fieldId)}
                    >
                      <Icon icon="Crosshair" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function BulkActionBar({ count, busy, onChoose, onClear }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" role="toolbar" aria-label={t`Bulk resolve conflicts`}>
      <span className="small font-bold">
        <Trans>{count} selected</Trans>
      </span>
      <Button
        size="sm"
        color="success"
        outline
        disabled={busy}
        onClick={() => onChoose('calculated')}
        {...getTestId('conflicts', '.', 'bulk-calculated')}
      >
        <Icon icon="Calculator" /> <Trans>Use calculated by us</Trans>
      </Button>
      <Button
        size="sm"
        color="warning"
        outline
        disabled={busy}
        onClick={() => onChoose('reported')}
        {...getTestId('conflicts', '.', 'bulk-reported')}
      >
        <Icon icon="FileArrowUp" /> <Trans>Use reported by you</Trans>
      </Button>
      <Button size="sm" color="link" disabled={busy} onClick={onClear}>
        <Trans>Clear selection</Trans>
      </Button>
    </div>
  );
}

/** `bySchedule` is a list of [scheduleTitle, selectedCount] pairs for the selected conflicts. */
function BulkResolveConfirmModal({ choice, bySchedule, busy, onConfirm, onCancel }) {
  const count = bySchedule.reduce((total, [, n]) => total + n, 0);
  const useCalculated = choice === 'calculated';
  const cancel = () => {
    if (!busy) onCancel();
  };

  return (
    <Modal isOpen={choice != null} toggle={cancel}>
      <ModalHeader toggle={cancel}>{useCalculated ? <Trans>Use calculated values</Trans> : <Trans>Use reported values</Trans>}</ModalHeader>
      <ModalBody>
        <p>
          {useCalculated ? (
            <Trans>Apply our calculated value to {count} fields?</Trans>
          ) : (
            <Trans>Keep the value you reported for {count} fields?</Trans>
          )}
        </p>
        <ul className="small mb-2">
          {bySchedule.map(([title, n]) => (
            <li key={title}>
              {title}: {n}
            </li>
          ))}
        </ul>
        {useCalculated && (
          <p className="small text-muted mb-0">
            <Trans>Fields resolved with the calculated value leave the conflicts list and can't be switched back here.</Trans>
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline disabled={busy} onClick={cancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color={useCalculated ? 'success' : 'warning'} disabled={busy} onClick={onConfirm} {...getTestId('conflicts', '.', 'bulk-confirm')}>
          {busy && <Spinner size="sm" className="mr-1" />}
          <Trans>Apply</Trans>
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function ConflictsPanel({ conflicts, formFieldsById, navigateToField, readOnly = false, className = 'p-3', emptyState = null }) {
  const [openSection, setOpenSection] = useState('');
  const toggle = id => setOpenSection(prev => (prev === id ? '' : id));
  const [selected, setSelected] = useState(() => new Set());
  const [confirmChoice, setConfirmChoice] = useState(null);
  const { resolve, pendingKeys } = useConflictResolver();
  const busy = pendingKeys.size > 0;

  const groups = useMemo(() => {
    const filtered = Object.entries(conflicts).filter(([, c]) => c != null && c !== false && c.resolved == null);
    const map = new Map();
    for (const [fieldId, conflict] of filtered) {
      const field = formFieldsById[fieldId];
      const key = field?.subformName ?? '__unknown__';
      if (!map.has(key)) map.set(key, { subformTitle: field?.subformTitle, items: [] });
      map.get(key).items.push({ fieldId, conflict, field });
    }
    return Array.from(map.entries());
  }, [conflicts, formFieldsById]);

  const allKeys = useMemo(() => groups.flatMap(([, g]) => g.items.map(item => item.fieldId)), [groups]);

  // Resolved conflicts drop out of `groups`; drop them from the selection too. Keys the server
  // could not resolve stay listed and stay selected, so the user can retry them.
  useEffect(() => {
    setSelected(prev => {
      const live = new Set(allKeys);
      const next = new Set([...prev].filter(key => live.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [allKeys]);

  const setKeys = (keys, on) =>
    setSelected(prev => {
      const next = new Set(prev);
      keys.forEach(key => (on ? next.add(key) : next.delete(key)));
      return next;
    });

  const applyBulk = async () => {
    await resolve([...selected], confirmChoice);
    setConfirmChoice(null);
  };

  const totalCount = allKeys.length;
  if (!totalCount) return emptyState;

  const selectedCount = selected.size;
  const selection = readOnly ? null : { selected, toggleKey: key => setKeys([key], !selected.has(key)) };
  const selectedBySchedule = groups
    .map(([groupKey, g]) => [g.subformTitle ?? groupKey, g.items.filter(item => selected.has(item.fieldId)).length])
    .filter(([, n]) => n > 0);

  return (
    <div className={className}>
      <div className="flex items-center gap-2 uppercase font-bold small text-muted mb-3">
        {selection && (
          <SelectCheckbox
            checked={selectedCount === totalCount}
            indeterminate={selectedCount > 0 && selectedCount < totalCount}
            onChange={e => setKeys(allKeys, e.target.checked)}
            label={t`Select all conflicts`}
            testId={getTestId('conflicts', '.', 'select-all')}
          />
        )}
        <Trans>Calculation Conflicts</Trans>
        <span className="badge bg-warning text-dark">{totalCount}</span>
      </div>
      {selection && selectedCount > 0 && (
        <BulkActionBar count={selectedCount} busy={busy} onChoose={setConfirmChoice} onClear={() => setSelected(new Set())} />
      )}
      <div className="flex flex-col gap-2">
        {groups.map(([groupKey, { subformTitle, items }]) => {
          const title = subformTitle ?? groupKey;
          const groupKeys = items.map(item => item.fieldId);
          const groupSelectedCount = groupKeys.filter(key => selected.has(key)).length;
          return (
            <AccordionItem
              key={groupKey}
              itemId={groupKey}
              isOpen={openSection === groupKey}
              toggle={toggle}
              prefixContent={
                selection && (
                  <SelectCheckbox
                    checked={groupSelectedCount === groupKeys.length}
                    indeterminate={groupSelectedCount > 0 && groupSelectedCount < groupKeys.length}
                    onChange={e => setKeys(groupKeys, e.target.checked)}
                    label={t`Select all conflicts in ${title}`}
                    testId={getTestId('conflicts', '.', 'select-group', '-', groupKey)}
                  />
                )
              }
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{title}</span>
                  <span className="badge bg-warning text-dark flex-shrink-0">{items.length}</span>
                </span>
              }
            >
              <ConflictGroupContent items={items} navigateToField={navigateToField} selection={selection} />
            </AccordionItem>
          );
        })}
      </div>
      <BulkResolveConfirmModal
        choice={confirmChoice}
        bySchedule={selectedBySchedule}
        busy={busy}
        onConfirm={applyBulk}
        onCancel={() => setConfirmChoice(null)}
      />
    </div>
  );
}

function NoConflictsRemaining() {
  return (
    <div className="text-center py-4 text-muted small">
      <Icon icon="CheckCircle" size={32} className="text-success mx-auto mb-2 block" />
      <Trans>No conflicts remaining</Trans>
    </div>
  );
}

// Sidebar wrapper — reads everything from UWEFormContext so SidebarComponents doesn't need to pass props
export function ConflictsSidebarPanel() {
  const ctx = useContext(UWEFormContext);
  const conflicts = ctx?.conflicts ?? {};
  const formFieldsById = ctx?.formFieldsById ?? {};
  const navigateToField = ctx?.navigateToField ?? (() => {});
  return (
    <ConflictsPanel
      conflicts={conflicts}
      formFieldsById={formFieldsById}
      navigateToField={navigateToField}
      readOnly={ctx?.formReadOnly ?? true}
      emptyState={<NoConflictsRemaining />}
    />
  );
}

export default ConflictsSidebarPanel;
